import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Icon, IconButton } from 'components';
import { Tooltip } from 'components/Tooltip/Tooltip.component';
import ActionMenu from 'components/ActionMenu';
import { Row } from 'components/layout';
import { Avatar } from 'components/Avatar/Avatar.component';
import { ButtonV3 } from 'components/ComponentV2';
import InviteConfirmationModal from 'pages/community/InviteConfirmationModal';
import {
  CommunityAttributes,
  CommunityTypes,
  ComunityInvitesOptions,
} from 'lib/models/community';
import { MessageModal } from '../MessageModal/MessageModal.component';
import InviteMembersModal from '../../InviteMembersConfirmationModal';

import styles from './ScreeningHeader.module.scss';

interface ScreeningHeaderProps {
  applications: CommunityAttributes[];
  selectionList: { [key: string]: CommunityAttributes };
  applicantDetails?: CommunityAttributes;
  disabledLeftArrow: boolean;
  disabledRightArrow: boolean;
  isScrolled: boolean;
  selectionCount: number;
  isEmpty: boolean;
  selectedCommunityOption: string;
  goToNext: () => void;
  goToPre: () => void;
  goBack: () => void;
  onInviteAttendee: (attendee: {
    first_name: string;
    last_name: string;
    email: string;
  }) => void;
  inviteAttendees: () => Promise<void>;
  inviteProfileViewers: () => Promise<void>;
  inviteEventViewers: () => Promise<void>;
  onSendMessage: (message: string, candidate_ids: string[]) => Promise<void>;
  bannerText: string;
}

export const ScreeningHeader: React.FC<ScreeningHeaderProps> = ({
  applicantDetails,
  selectionList,
  disabledLeftArrow,
  disabledRightArrow,
  isScrolled,
  isEmpty,
  selectedCommunityOption,
  goToNext,
  goToPre,
  goBack,
  selectionCount,
  onInviteAttendee,
  inviteAttendees,
  inviteProfileViewers,
  inviteEventViewers,
  onSendMessage,
  bannerText,
}: ScreeningHeaderProps) => {
  const { email, name, avatar_url } = applicantDetails || {};

  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [showInviteConfirmationModal, setShowInviteConfirmationModal] =
    useState(false);
  const [inviteOption, setInviteOption] = useState('');
  useEffect(() => {
    setShowBanner(true);
  }, [bannerText]);
  const handleClose = () => {
    goBack();
  };

  const getNameFromEmail = (email?: string) => {
    return email && email.split('@')[0];
  };

  const messageOptions = [
    {
      id: '1',
      label: `Message ${name || getNameFromEmail(email) || ''}`,
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event?.stopPropagation();
        setIsBulkMode(false);
        setIsMessageModalOpen(true);
      },
    },
    ...(selectionCount > 0
      ? [
          {
            id: '2',
            label: `Message selected (${selectionCount})`,
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              event?.stopPropagation();
              setIsBulkMode(true);
              setIsMessageModalOpen(true);
            },
          },
        ]
      : []),
  ];
  const [showConfirmation, setShowConfirmation] = useState(false);
  const handleDoneClick = async (doneClicked: boolean) => {
    try {
      if (doneClicked) {
        if (inviteOption === ComunityInvitesOptions.INVITE_ALL_ATTENDEES) {
          await inviteAttendees();
          setShowConfirmation(true);
        } else if (inviteOption === ComunityInvitesOptions.INVITE_ATTENDEES) {
          if (applicantDetails && !applicantDetails?.invited) {
            await onInviteAttendee({
              first_name: applicantDetails?.first_name || '',
              last_name: applicantDetails?.last_name || '',
              email: applicantDetails?.email,
            });
            setShowConfirmation(true);
          }
        } else if (
          inviteOption === ComunityInvitesOptions.INVITE_ALL_EVENT_VIEWERS
        ) {
          await inviteEventViewers();
          setShowConfirmation(true);
        } else if (
          inviteOption === ComunityInvitesOptions.INVITE_ALL_PROFILE_VIEWERS
        ) {
          await inviteProfileViewers();
          setShowConfirmation(true);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getInviteOptions = (selectedApplication?: CommunityAttributes) => {
    return [
      {
        id: '1',
        label: `Invite ${name || getNameFromEmail(email) || ''}`,
        disabled: selectedApplication?.invited,
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e?.preventDefault();
          e?.stopPropagation();
          setInviteOption(ComunityInvitesOptions.INVITE_ATTENDEES);
          setShowInviteConfirmationModal(true);
        },
      },
      ...(selectedCommunityOption === CommunityTypes.ATTENDEES
        ? [
            {
              id: '2',
              label: 'Invite all Attendees',
              onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e?.preventDefault();
                e?.stopPropagation();
                setInviteOption(ComunityInvitesOptions.INVITE_ALL_ATTENDEES);
                setShowInviteConfirmationModal(true);
              },
            },
          ]
        : []),
      ...(selectedCommunityOption === CommunityTypes.EVENT_VIEWS
        ? [
            {
              id: '3',
              label: 'Invite all Event Viewers',
              onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e?.preventDefault();
                e?.stopPropagation();
                setInviteOption(
                  ComunityInvitesOptions.INVITE_ALL_EVENT_VIEWERS
                );
                setShowInviteConfirmationModal(true);
              },
            },
          ]
        : []),
      ...(selectedCommunityOption === CommunityTypes.PROFILE_VIEWS
        ? [
            {
              id: '4',
              label: 'Invite all Profile Viewers',
              onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e?.preventDefault();
                e?.stopPropagation();
                setInviteOption(
                  ComunityInvitesOptions.INVITE_ALL_PROFILE_VIEWERS
                );
                setShowInviteConfirmationModal(true);
              },
            },
          ]
        : []),
    ];
  };

  const toggleMessageModal = () => {
    setIsMessageModalOpen(!isMessageModalOpen);
  };

  const onMessageAction = async (message: string) => {
    const selectedIds = Object.keys(selectionList);

    if (isBulkMode && selectedIds?.length) {
      await onSendMessage(message, selectedIds);
    } else if (applicantDetails?.id) {
      await onSendMessage(message, [applicantDetails?.id]);
    }
    toggleMessageModal();
  };

  return (
    <>
      <div className={styles.ScreeningHeader}>
        <div className={styles.infoSection}>
          <div className={styles.titleSection}>
            <IconButton
              size="medium"
              iconName="close"
              className={styles.closeIcon}
              onClick={handleClose}
            />
            <div>
              <p className={styles.modalTitle}>Community</p>
            </div>
          </div>
          <div className={styles.navigation}>
            <Tooltip content="Previous" color="dark">
              <div
                className={cn(styles.arrowIcon, {
                  [styles.disabledIcon]: disabledLeftArrow,
                })}
                onClick={goToPre}
              >
                <Icon iconName="icon_arrow-left" />
              </div>
            </Tooltip>
            <Tooltip content="Next" color="dark">
              <div
                className={cn(styles.arrowIcon, {
                  [styles.disabledIcon]: disabledRightArrow,
                })}
                onClick={goToNext}
              >
                <Icon iconName="icon_arrow-forward" />
              </div>
            </Tooltip>
            {isScrolled && !isEmpty ? (
              <div className={styles.applicantData}>
                <Avatar
                  name={name || email || ''}
                  size="medium"
                  icon={avatar_url}
                />
                <p className={styles.applicantName}>
                  {name || getNameFromEmail(email) || ''}
                </p>
              </div>
            ) : (
              <Tooltip
                content="Hit Left or Right on your keyboard to navigate"
                color="dark"
              >
                <p className={styles.navigationTitle}>
                  Use your keyboard to navigate through candidates faster.
                </p>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={styles.actionSection}>
          {selectedCommunityOption !== CommunityTypes.SUBSCRIBED &&
          selectedCommunityOption !== CommunityTypes.SUBSCRIPTION_REQUESTS ? (
            <ActionMenu
              listClassName={styles.options}
              className={styles.dropdownInvite}
              disabled={false}
              actionElement={
                <Row
                  align="center"
                  justify="space-between"
                  columnGap={8}
                  className={cn(
                    styles.HeadingActionsCreate,
                    styles.HeadingActionsCreatePrimary
                  )}
                >
                  Invite to join
                  <Icon iconName="icon_arrow-down" size="xxsmallplus" />
                </Row>
              }
              triggerButtonClassName={cn(styles.HeadingActionsActionMenu, {
                [styles.disabled]: false,
              })}
              options={getInviteOptions(applicantDetails)}
            ></ActionMenu>
          ) : null}

          <ActionMenu
            listClassName={styles.options}
            className={styles.dropdownMessage}
            disabled={false}
            actionElement={
              <Row
                align="center"
                justify="space-between"
                columnGap={8}
                className={cn(
                  styles.HeadingActionsCreate,
                  styles.HeadingActionsCreatePrimary
                )}
              >
                <Icon
                  iconName="icon_send-message"
                  size="small"
                  className={styles.MessageIcon}
                />
                Message
                <Icon iconName="icon_arrow-down" size="xxsmallplus" />
              </Row>
            }
            triggerButtonClassName={cn(styles.HeadingActionsActionMenu, {
              [styles.disabled]: false,
            })}
            options={messageOptions}
          ></ActionMenu>
        </div>

        {isMessageModalOpen ? (
          <MessageModal
            isOpen={isMessageModalOpen}
            type={selectedCommunityOption}
            onClose={toggleMessageModal}
            isBulkMode={selectionCount && isBulkMode ? true : false}
            selectionCount={selectionCount}
            candidateName={
              applicantDetails
                ? (applicantDetails?.name ||
                    applicantDetails?.first_name ||
                    applicantDetails?.last_name ||
                    applicantDetails?.email?.split('@')?.[0]) ??
                  ''
                : ''
            }
            onSendMessage={onMessageAction}
          />
        ) : null}
      </div>
      {bannerText && showBanner && (
        <div className={styles.bannerText}>
          <p>
            <Icon iconName="icon_info" />
            {bannerText}
          </p>
          <ButtonV3
            onClick={() => setShowBanner(false)}
            color="secondary"
            size="small"
          >
            Ok
          </ButtonV3>
        </div>
      )}
      <InviteMembersModal
        isSingleMember={true}
        open={showInviteConfirmationModal}
        onClose={() => setShowInviteConfirmationModal(false)}
        onDoneClick={handleDoneClick}
      />
      <InviteConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        isSingleMember={true}
      />
    </>
  );
};

ScreeningHeader.displayName = 'ScreeningHeader';
